import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { 
  ArrowLeft, 
  Settings, 
  Users, 
  Calendar, 
  FileText, 
  Download, 
  Upload, 
  CheckCircle, 
  X, 
  Copy, 
  CheckIcon, 
  ExternalLinkIcon, 
  CreditCard,
  QrCode,
  Printer,
  Clock,
  Eye,
  EyeOff,
  BookOpen,
  Star,
  StarOff,
  TrendingUp,
  Heart,
  Share2,
  AlertCircle,
  Image as ImageIcon,
  User,
  MapPin,
  Globe,
  UserCircle,
  Award,
  Building,
  Grid3X3,
  Mail,
  Phone,
  DollarSign,
  Send,
  Smartphone,
  ShoppingCart,
  ShoppingBag,
  BarChart3,
  Trophy,
  Vote,
  Info,
  Image,
  Ticket,
  RefreshCw,
  UserCheck,
  Search,
  Percent,
  Tag,
  Link,
  Save,
  Trash2,
  Filter,
  Edit,
  MoreVertical,
  ExternalLink,
  Volume2,
  Plus
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { getEventDetail, getEventCollection, getEventStories, getEventAttendees, createArtworkStory, updateStoryFeatured, getEventTicketTypes, getEventTicketSales, createTicketType, updateTicketType, deleteTicketType } from '../utils/eventsAdminApi';
import { EventbriteApiService } from '../utils/eventApiHelpers';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';

import '../styles/EventDetailAdmin.css';
import EventMerchTab from './EventMerchTab';
import EventPrintSignupsTab from './EventPrintSignupsTab';

const EventDetailAdmin = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Main state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('tickets');
  const [event, setEvent] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [stories, setStories] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArtwork, setSelectedArtwork] = useState(null);

  // Ticket Management State
  const [ticketTypes, setTicketTypes] = useState([]);
  const [ticketSales, setTicketSales] = useState([]);
  const [ticketAnalytics, setTicketAnalytics] = useState(null);
  const [printSignupStats, setPrintSignupStats] = useState({
    total: 0,
    printLabSupport: 0,
    printsOnHand: 0,
  });
  const [showTicketTypeModal, setShowTicketTypeModal] = useState(false);
  const [editingTicketType, setEditingTicketType] = useState(null);
  const [savingTicket, setSavingTicket] = useState(false);
  const [salesPage, setSalesPage] = useState(1);
  const SALES_PER_PAGE = 10;
  
  // Promo Code State
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoCodeModal, setShowPromoCodeModal] = useState(false);
  const [savingPromoCode, setSavingPromoCode] = useState(false);
  const [promoCodeForm, setPromoCodeForm] = useState({
    promoCode: '',
    discountType: 'percentage',
    discountValue: '',
    maxRedemptions: '',
    validUntil: ''
  });
  
  // Artist Promo Code State
  const [showArtistPromoModal, setShowArtistPromoModal] = useState(false);
  const [generatingArtistPromos, setGeneratingArtistPromos] = useState(false);
  const [artistPromoResults, setArtistPromoResults] = useState(null);
  const [artistPromoTiers, setArtistPromoTiers] = useState([
    { discountType: 'percentage', discountValue: 20, codesPerArtist: 1, maxRedemptionsPerCode: 10 }
  ]);
  const [artistPromoExpiry, setArtistPromoExpiry] = useState('');
  
  const [ticketTypeForm, setTicketTypeForm] = useState({
    ticket_type_name: '',
    description: '',
    regular_price: '',
    early_bird_price: '',
    early_bird_deadline: '',
    tickets_available: '',
    sale_start_date: '',
    sale_end_date: '',
    is_active: true
  });
  
  // Entertainment Tax State
  const [entertainmentTaxEnabled, setEntertainmentTaxEnabled] = useState(false);
  const [savingTax, setSavingTax] = useState(false);

  // Status management
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Eventbrite sync
  const [syncingEventbrite, setSyncingEventbrite] = useState(false);
  const [eventbriteStatus, setEventbriteStatus] = useState(null);
  const [userToken, setUserToken] = useState(null);

  const [selectedArtworkStories, setSelectedArtworkStories] = useState(null);

  // Loading stages
  const [loadingStages, setLoadingStages] = useState({
    event: true,
    artworks: true,
    stories: true,
    attendees: true,
    tickets: true
  });
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Get user token for Eventbrite API
  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        setUserToken(session.access_token);
      }
    };
    getSession();
  }, []);

  // Helper function to determine context and build appropriate navigation paths
  const getNavigationContext = () => {
    const isCuratorContext = location.pathname.startsWith('/curator');
    return {
      isCurator: isCuratorContext,
      eventsPath: isCuratorContext ? '/curator/events' : '/admin/events',
      storiesPath: isCuratorContext ? '/curator/stories' : '/admin/stories', // May need to add curator stories route
      collectionsPath: isCuratorContext ? '/curator/collections' : '/admin/collections'
    };
  };

  // Load event data
  useEffect(() => {
    loadEventData();
  }, [eventId]);

  const loadEventData = async () => {
    try {
      console.log('Loading event data for eventId:', eventId);
      setLoading(true);
      setError(null);
      setLoadingProgress(0);

      // Load event details
      console.log('Loading event details...');
      const { data: eventData, error: eventError } = await getEventDetail(eventId);
      if (eventError) throw eventError;
      console.log('Event data loaded:', eventData);
      setEvent(eventData);
      setEntertainmentTaxEnabled(eventData?.entertainment_tax_enabled || false);
      setLoadingStages(prev => ({ ...prev, event: false }));
      setLoadingProgress(25);

      // Load collection artworks
      console.log('Loading collection artworks...');
      const { data: artworksData, error: artworksError } = await getEventCollection(eventId);
      if (artworksError) throw artworksError;
      console.log('Artworks data loaded:', artworksData);
      
      // Load story information for all artworks in one query
      if (artworksData && artworksData.length > 0) {
        console.log('Loading story information for artworks...');
        
        // Get all artwork IDs
        const artworkIds = artworksData.map(artwork => artwork.vc_id);
        
        // Fetch all stories for these artworks in one query
        const { data: allStories, error: storiesError } = await supabase
          .from('artspeaks_stories')
          .select(`
            story_id, title, created_at, featured, vc_id,
            story_writer:users!artist_uuid (uuid, nickname, first_name, last_name)
          `)
          .in('vc_id', artworkIds)
          .eq('event_id', eventId);

        if (storiesError) {
          console.error('Error loading stories:', storiesError);
        }

        // Group stories by artwork ID
        const storiesByArtwork = {};
        if (allStories) {
          allStories.forEach(story => {
            if (!storiesByArtwork[story.vc_id]) {
              storiesByArtwork[story.vc_id] = [];
            }
            storiesByArtwork[story.vc_id].push(story);
          });
        }

        // Map stories to artworks
        const artworksWithStories = artworksData.map(artwork => {
          const artworkStories = storiesByArtwork[artwork.vc_id] || [];
          return {
            ...artwork,
            stories: artworkStories,
            story_count: artworkStories.length,
            has_stories: artworkStories.length > 0
          };
        });

        setArtworks(artworksWithStories);
        console.log('Artworks with story data loaded:', artworksWithStories);
      } else {
        setArtworks(artworksData || []);
      }
      
      setLoadingStages(prev => ({ ...prev, artworks: false }));
      setLoadingProgress(50);

      // Load stories
      console.log('Loading stories for eventId:', eventId, 'type:', typeof eventId);
      const { data: storiesData, error: storiesError } = await getEventStories(eventId);
      if (storiesError) throw storiesError;
      console.log('Stories data loaded:', storiesData);
      setStories(storiesData || []);
      setLoadingStages(prev => ({ ...prev, stories: false }));
      setLoadingProgress(75);

      // Load attendees
      console.log('Loading attendees...');
      const { data: attendeesData, error: attendeesError } = await getEventAttendees(eventId);
      if (attendeesError) throw attendeesError;
      console.log('Attendees data loaded:', attendeesData);
      setAttendees(attendeesData || []);
      setLoadingStages(prev => ({ ...prev, attendees: false }));
      setLoadingProgress(80);

      // Load ticket types
      console.log('Loading ticket types...');
      let ticketTypesData = [];
      try {
        ticketTypesData = await getEventTicketTypes(eventId);
        setTicketTypes(ticketTypesData || []);
      } catch (ticketTypesError) {
        console.error('Error loading ticket types:', ticketTypesError);
      }
      
      // Load ticket sales
      console.log('Loading ticket sales...');
      try {
        const salesData = await getEventTicketSales(eventId);
        console.log('Ticket sales loaded:', salesData?.length || 0, 'records');
        setTicketSales(salesData || []);
        // Calculate analytics
        calculateTicketAnalytics(salesData || [], ticketTypesData || []);
      } catch (salesError) {
        console.error('Error loading ticket sales:', salesError);
      }
      setLoadingStages(prev => ({ ...prev, tickets: false }));
      setLoadingProgress(95);
      
      // Load promo codes
      console.log('Loading promo codes...');
      await loadPromoCodes(eventId);

      // Load print signup readiness summary
      await loadPrintSignupStats(eventId);
      setLoadingProgress(100);

    } catch (err) {
      console.error('Error loading event data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPrintSignupStats = async (eventIdToLoad) => {
    try {
      const { data, error } = await supabase
        .from('event_print_signups')
        .select('wants_scanning, wants_open_house, bringing_own_prints')
        .eq('event_id', parseInt(eventIdToLoad));

      if (error) throw error;

      const rows = data || [];
      const total = rows.length;
      const printLabSupport = rows.filter(
        row => row.wants_scanning || row.wants_open_house,
      ).length;
      const printsOnHand = rows.filter(row => row.bringing_own_prints).length;

      setPrintSignupStats({ total, printLabSupport, printsOnHand });
    } catch (err) {
      console.error('Error loading print signup stats:', err);
      setPrintSignupStats({ total: 0, printLabSupport: 0, printsOnHand: 0 });
    }
  };
  
  // Load promo codes for event
  const loadPromoCodes = async (eventIdToLoad) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage_event_promo_codes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            action: 'list',
            eventId: parseInt(eventIdToLoad)
          })
        }
      );
      
      const data = await response.json();
      if (data.success && data.promoCodes) {
        setPromoCodes(data.promoCodes);
      }
    } catch (err) {
      console.error('Error loading promo codes:', err);
    }
  };
  
  // Create new promo code
  const handleCreatePromoCode = async () => {
    if (!promoCodeForm.promoCode || !promoCodeForm.discountValue) {
      alert('Please enter a promo code and discount value');
      return;
    }
    
    setSavingPromoCode(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage_event_promo_codes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            action: 'create',
            eventId: parseInt(eventId),
            promoCode: promoCodeForm.promoCode,
            discountType: promoCodeForm.discountType,
            discountValue: parseFloat(promoCodeForm.discountValue),
            maxRedemptions: promoCodeForm.maxRedemptions ? parseInt(promoCodeForm.maxRedemptions) : null,
            validUntil: promoCodeForm.validUntil || null
          })
        }
      );
      
      const data = await response.json();
      if (data.success) {
        setShowPromoCodeModal(false);
        setPromoCodeForm({ promoCode: '', discountType: 'percentage', discountValue: '', maxRedemptions: '', validUntil: '' });
        await loadPromoCodes(eventId);
        setEventbriteStatus({ success: true, message: `Promo code ${data.promoCode.code} created!` });
        setTimeout(() => setEventbriteStatus(null), 3000);
      } else {
        alert('Error creating promo code: ' + data.error);
      }
    } catch (err) {
      console.error('Error creating promo code:', err);
      alert('Failed to create promo code: ' + err.message);
    } finally {
      setSavingPromoCode(false);
    }
  };
  
  // Deactivate promo code
  const handleDeactivatePromoCode = async (promoCodeId, code) => {
    if (!confirm(`Are you sure you want to deactivate promo code ${code}?`)) return;
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage_event_promo_codes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            action: 'deactivate',
            eventId: parseInt(eventId),
            promoCodeId: promoCodeId
          })
        }
      );
      
      const data = await response.json();
      if (data.success) {
        await loadPromoCodes(eventId);
        setEventbriteStatus({ success: true, message: `Promo code ${code} deactivated` });
        setTimeout(() => setEventbriteStatus(null), 3000);
      } else {
        alert('Error deactivating promo code: ' + data.error);
      }
    } catch (err) {
      console.error('Error deactivating promo code:', err);
      alert('Failed to deactivate promo code');
    }
  };

  // Generate artist promo codes in bulk
  const handleGenerateArtistPromos = async () => {
    if (!event?.collection_id) {
      alert('This event has no linked collection');
      return;
    }

    setGeneratingArtistPromos(true);
    setArtistPromoResults(null);
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage_event_promo_codes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            action: 'bulk_create_artist_promos',
            eventId: parseInt(eventId),
            tiers: artistPromoTiers.map(t => ({
              discountType: t.discountType,
              discountValue: parseFloat(t.discountValue),
              codesPerArtist: parseInt(t.codesPerArtist),
              maxRedemptionsPerCode: t.maxRedemptionsPerCode ? parseInt(t.maxRedemptionsPerCode) : null,
            })),
            validUntil: artistPromoExpiry || null,
          })
        }
      );

      const data = await response.json();
      if (data.success) {
        setArtistPromoResults(data);
        await loadPromoCodes(eventId);
        setEventbriteStatus({ success: true, message: `Created ${data.totalCodesCreated} artist promo codes!` });
        setTimeout(() => setEventbriteStatus(null), 5000);
      } else {
        alert('Error generating artist promos: ' + data.error);
      }
    } catch (err) {
      console.error('Error generating artist promos:', err);
      alert('Failed to generate artist promos: ' + err.message);
    } finally {
      setGeneratingArtistPromos(false);
    }
  };

  // Group promo codes by artist
  const artistPromoCodes = promoCodes.filter(p => p.artist_uuid);
  const generalPromoCodes = promoCodes.filter(p => !p.artist_uuid);
  const artistPromosByArtist = artistPromoCodes.reduce((acc, promo) => {
    const key = promo.artist_uuid;
    if (!acc[key]) acc[key] = [];
    acc[key].push(promo);
    return acc;
  }, {});

  // Story Action Handlers
  const handleViewStory = (story) => {
    const { storiesPath } = getNavigationContext();
    navigate(`${storiesPath}/${story.story_id}`);
  };

  const handleEditStory = (story) => {
    const { storiesPath } = getNavigationContext();
    navigate(`${storiesPath}/${story.story_id}/edit`);
  };

  const handleToggleFeatured = async (storyId, currentFeatured) => {
    try {
      const { data, error } = await updateStoryFeatured(storyId, !currentFeatured);
      
      if (error) throw error;

      // Refresh stories
      const { data: storiesData } = await getEventStories(eventId);
      setStories(storiesData || []);
      
      // Show success notification
      alert(`Story ${currentFeatured ? 'unfeatured' : 'featured'} successfully!`);
      
    } catch (error) {
      console.error('Error toggling story featured status:', error);
      alert('Failed to update story: ' + error.message);
    }
  };

  // Calculate ticket analytics
  const calculateTicketAnalytics = (sales, types) => {
    const completedSales = sales.filter(s => s.payment_status === 'completed');
    const totalRevenue = completedSales.reduce((sum, s) => sum + (s.amount_paid || 0), 0);
    const totalTicketsSold = completedSales.reduce((sum, s) => sum + (s.quantity || 1), 0);
    const totalTicketsAvailable = types.reduce((sum, t) => sum + (t.tickets_available || 0), 0);
    const checkedInCount = completedSales.filter(s => s.checked_in).length;
    
    const salesByType = types.map(type => {
      const typeSales = completedSales.filter(s => s.ticket_type_id === type.id);
      const typeRevenue = typeSales.reduce((sum, s) => sum + (s.amount_paid || 0), 0);
      const typeQuantity = typeSales.reduce((sum, s) => sum + (s.quantity || 1), 0);
      return {
        ...type,
        sold: typeQuantity,
        revenue: typeRevenue,
        remaining: type.tickets_available - type.tickets_sold
      };
    });

    setTicketAnalytics({
      totalRevenue,
      totalTicketsSold,
      totalTicketsAvailable,
      checkedInCount,
      salesByType,
      recentSales: sales.slice(0, 10)
    });
  };

  // Toggle entertainment tax
  const handleToggleEntertainmentTax = async (enabled) => {
    setSavingTax(true);
    try {
      const { error } = await supabase
        .from('events')
        .update({
          entertainment_tax_enabled: enabled,
          entertainment_tax_rate: enabled ? 0.1025 : 0
        })
        .eq('id', parseInt(eventId));
      if (error) throw error;
      setEntertainmentTaxEnabled(enabled);
      setEvent(prev => ({ ...prev, entertainment_tax_enabled: enabled, entertainment_tax_rate: enabled ? 0.1025 : 0 }));
    } catch (err) {
      console.error('Failed to update entertainment tax:', err);
      alert('Failed to update tax setting');
    } finally {
      setSavingTax(false);
    }
  };

  // Reset ticket type form
  const resetTicketTypeForm = () => {
    setTicketTypeForm({
      ticket_type_name: '',
      description: '',
      regular_price: '',
      early_bird_price: '',
      early_bird_deadline: '',
      tickets_available: '',
      sale_start_date: '',
      sale_end_date: '',
      is_active: true
    });
  };

  // Open edit ticket type modal
  const openEditTicketType = (ticketType) => {
    setEditingTicketType(ticketType);
    setTicketTypeForm({
      ticket_type_name: ticketType.ticket_type_name || '',
      description: ticketType.description || '',
      regular_price: ticketType.regular_price?.toString() || '',
      early_bird_price: ticketType.early_bird_price?.toString() || '',
      early_bird_deadline: ticketType.early_bird_deadline?.slice(0, 16) || '',
      tickets_available: ticketType.tickets_available?.toString() || '',
      sale_start_date: ticketType.sale_start_date?.slice(0, 16) || '',
      sale_end_date: ticketType.sale_end_date?.slice(0, 16) || '',
      is_active: ticketType.is_active !== false
    });
    setShowTicketTypeModal(true);
  };

  // Save ticket type
  const handleSaveTicketType = async () => {
    if (!ticketTypeForm.ticket_type_name || !ticketTypeForm.regular_price) {
      alert('Please fill in the required fields');
      return;
    }

    setSavingTicket(true);
    try {
      const ticketData = {
        event_id: parseInt(eventId),
        ticket_type_name: ticketTypeForm.ticket_type_name,
        description: ticketTypeForm.description || null,
        regular_price: parseFloat(ticketTypeForm.regular_price) || 0,
        early_bird_price: ticketTypeForm.early_bird_price ? parseFloat(ticketTypeForm.early_bird_price) : null,
        early_bird_deadline: ticketTypeForm.early_bird_deadline || null,
        tickets_available: parseInt(ticketTypeForm.tickets_available) || 100,
        tickets_sold: editingTicketType?.tickets_sold || 0,
        sale_start_date: ticketTypeForm.sale_start_date || null,
        sale_end_date: ticketTypeForm.sale_end_date || null,
        is_active: ticketTypeForm.is_active !== false
      };

      if (editingTicketType) {
        // Update existing
        const { error } = await supabase
          .from('event_ticket_types')
          .update(ticketData)
          .eq('id', editingTicketType.id);
        if (error) throw error;
      } else {
        // Create new - assign temporary Stripe IDs (should be created via Edge Function in production)
        const { error } = await supabase
          .from('event_ticket_types')
          .insert({
            ...ticketData,
            stripe_product_id: `pending_${Date.now()}`,
            stripe_price_id: `pending_${Date.now()}`
          });
        if (error) throw error;
      }

      setShowTicketTypeModal(false);
      setEditingTicketType(null);
      resetTicketTypeForm();
      loadEventData();
    } catch (err) {
      console.error('Error saving ticket type:', err);
      alert('Failed to save ticket type: ' + err.message);
    } finally {
      setSavingTicket(false);
    }
  };

  // Delete ticket type
  const handleDeleteTicketType = async (ticketTypeId) => {
    if (!confirm('Are you sure you want to delete this ticket type? This cannot be undone.')) return;
    
    try {
      const { error } = await supabase
        .from('event_ticket_types')
        .delete()
        .eq('id', ticketTypeId);
      
      if (error) throw error;
      loadEventData();
    } catch (err) {
      console.error('Error deleting ticket type:', err);
      alert('Failed to delete ticket type: ' + err.message);
    }
  };

  // Update event status
  const handleStatusChange = async (newStatus) => {
    const statusLabels = {
      draft: 'Draft',
      active: 'Active',
      upcoming: 'Upcoming',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    
    if (newStatus !== 'draft' && event.status === 'draft') {
      if (!confirm(`Are you sure you want to publish this event as "${statusLabels[newStatus]}"? This will make it visible to users.`)) return;
    } else if (newStatus === 'cancelled') {
      if (!confirm('Are you sure you want to cancel this event? Attendees will be notified.')) return;
    }
    
    setUpdatingStatus(true);
    try {
      const { error } = await supabase
        .from('events')
        .update({ status: newStatus })
        .eq('id', eventId);
      
      if (error) throw error;
      
      setEvent(prev => ({ ...prev, status: newStatus }));
      setEventbriteStatus({ success: true, message: `Event status updated to "${statusLabels[newStatus]}"` });
      setTimeout(() => setEventbriteStatus(null), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setEventbriteStatus({ success: false, message: 'Failed to update status: ' + err.message });
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Eventbrite sync
  const handleEventbriteSync = async () => {
    if (!event?.eventbrite_id || !userToken) {
      alert('No Eventbrite ID linked to this event or not authenticated');
      return;
    }

    setSyncingEventbrite(true);
    try {
      const apiService = new EventbriteApiService(userToken);
      await apiService.syncEventAttendees(event.eventbrite_id);
      setEventbriteStatus({ success: true, message: 'Eventbrite sync completed!' });
      loadEventData();
      setTimeout(() => setEventbriteStatus(null), 3000);
    } catch (err) {
      console.error('Eventbrite sync error:', err);
      setEventbriteStatus({ success: false, message: 'Sync failed: ' + err.message });
    } finally {
      setSyncingEventbrite(false);
    }
  };

  // Copy ticket link
  const copyTicketLink = (ticketTypeId) => {
    const url = `https://vibrantcast.com/events/${eventId}/tickets${ticketTypeId ? `?type=${ticketTypeId}` : ''}`;
    navigator.clipboard.writeText(url);
    alert('Ticket purchase link copied!');
  };

  // Print check-in checklist
  const handlePrintChecklist = () => {
    const completedSales = ticketSales.filter(s => s.payment_status === 'completed');
    const eventDate = event?.event_start 
      ? new Date(event.event_start).toLocaleDateString('en-US', { 
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
        })
      : '';
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${event?.name || 'Event'} - Check-in List</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; }
          .header { margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
          .header h1 { font-size: 18px; margin-bottom: 4px; }
          .header p { font-size: 12px; color: #666; }
          .stats { display: flex; gap: 20px; margin-bottom: 15px; font-size: 12px; }
          .stats span { background: #f0f0f0; padding: 4px 8px; border-radius: 4px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: 600; }
          tr:nth-child(even) { background: #fafafa; }
          .checkbox { width: 20px; height: 20px; border: 2px solid #333; border-radius: 3px; }
          .source { font-size: 9px; padding: 2px 4px; border-radius: 3px; margin-left: 4px; }
          .source.eb { background: #f6682f; color: white; }
          .source.vc { background: #912aa6; color: white; }
          .footer { margin-top: 20px; font-size: 10px; color: #999; text-align: center; }
          @media print { 
            body { padding: 10px; } 
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${event?.name || 'Event Check-in List'}</h1>
          <p>${eventDate} • ${event?.gallery_name || ''}</p>
        </div>
        <div class="stats">
          <span><strong>${completedSales.length}</strong> Total Tickets</span>
          <span><strong>${completedSales.filter(s => s.checked_in).length}</strong> Already Checked In</span>
          <span><strong>${completedSales.filter(s => !s.checked_in).length}</strong> Pending</span>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 30px;">✓</th>
              <th>Name</th>
              <th>Email</th>
              <th>Ticket Type</th>
              <th>Qty</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            ${completedSales.map(sale => `
              <tr>
                <td><div class="checkbox">${sale.checked_in ? '✓' : ''}</div></td>
                <td>${sale.customer_name || (sale.user ? sale.user.first_name + ' ' + sale.user.last_name : 'Guest')}</td>
                <td>${sale.customer_email || sale.user?.email || '—'}</td>
                <td>${sale.ticket_type_name || sale.ticket_type?.ticket_type_name || 'General'}</td>
                <td>${sale.quantity || 1}</td>
                <td><span class="source ${sale.source === 'eventbrite' ? 'eb' : 'vc'}">${sale.source === 'eventbrite' ? 'EB' : 'VC'}</span></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">
          Printed ${new Date().toLocaleString()} • VibrantCast
        </div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Format helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Get available tabs
  const getAvailableTabs = () => {
    const tabs = [
      { id: 'tickets', label: 'Tickets', icon: Ticket },
      { id: 'attendees', label: 'Attendees', icon: Users },
      { id: 'merchandise', label: 'Merchandise', icon: ShoppingBag },
      {
        id: 'print_signups',
        label: `Print Signups (${printSignupStats.total})`,
        icon: Printer,
      },
    ];

    return tabs;
  };

  // Filter artworks based on search
  const getFilteredArtworks = () => {
    if (!searchTerm.trim()) return artworks;
    
    const searchTermLower = searchTerm.toLowerCase();
    return artworks.filter(artwork => 
      artwork.title?.toLowerCase().includes(searchTermLower) ||
      artwork.artist?.nickname?.toLowerCase().includes(searchTermLower) ||
      artwork.artist?.first_name?.toLowerCase().includes(searchTermLower) ||
      artwork.artist?.last_name?.toLowerCase().includes(searchTermLower)
    );
  };

  // Story Card Component
  const StoryCard = ({ story }) => (
    <div className="story-card">
      <div className="story-header">
        <div className="story-title-section">
          <h4 className="story-title">{story.title}</h4>
          {story.is_featured && (
            <span className="featured-badge">
              <Star className="w-3 h-3" />
              Featured
            </span>
          )}
        </div>
        <div className="story-actions">
          <button 
            className="story-action-btn"
            onClick={() => handleViewStory(story)}
            title="View Story Details"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button 
            className="story-action-btn"
            onClick={() => handleEditStory(story)}
            title="Edit Story"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button 
            className={`story-action-btn ${story.featured ? 'featured' : ''}`}
            onClick={() => handleToggleFeatured(story.story_id, story.featured)}
            title={story.featured ? "Unfeature Story" : "Feature Story"}
          >
            <Star className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="story-content">{story.content}</p>
      <div className="story-meta">
        <span className="story-artist">{story.artist_name}</span>
        <span className="story-date">{new Date(story.created_at).toLocaleDateString()}</span>
        {story.story_type && (
          <span className="story-type">{story.story_type}</span>
        )}
      </div>
    </div>
  );

  // Artwork Card Component
  const ArtworkCard = ({ artwork }) => (
    <div className="artwork-card">
      <div className="artwork-image" onClick={() => setSelectedArtwork(artwork)}>
        <img src={artwork.cover_image} alt={artwork.title} />
        <div className="artwork-overlay">
          <Eye className="overlay-icon" />
        </div>
        {artwork.has_stories && (
          <div className="story-badge">
            <FileText className="w-3 h-3" />
            {artwork.story_count}
          </div>
        )}
      </div>
      
      <div className="artwork-info">
        <h4 className="artwork-title">{artwork.title}</h4>
        <p className="artwork-artist">
          {artwork.artist ? 
            (artwork.artist.nickname || `${artwork.artist.first_name} ${artwork.artist.last_name}`) :
            artwork.artist_name || 'Unknown Artist'
          }
        </p>
        
        {/* Story Status */}
        {artwork.has_stories && (
          <div className="story-status">
            <span className="story-count-text">
              {artwork.story_count} {artwork.story_count === 1 ? 'story' : 'stories'}
            </span>
            {artwork.stories.some(story => story.featured) && (
              <span className="featured-indicator">
                <Star className="w-3 h-3" />
              </span>
            )}
          </div>
        )}
        
        <div className="artwork-actions">
          {artwork.has_stories ? (
            <div className="story-action-buttons">
              <button 
                onClick={() => {
                  if (artwork.story_count === 1) {
                    // Navigate to the single story
                    const { storiesPath } = getNavigationContext();
                    navigate(`${storiesPath}/${artwork.stories[0].story_id}`);
                  } else {
                    // Show stories selection modal
                    setSelectedArtworkStories(artwork);
                  }
                }}
                className="btn-primary"
                title={artwork.story_count === 1 ? "View Story" : "View Stories"}
              >
                <Eye className="w-4 h-4" />
                {artwork.story_count === 1 ? 'View Story' : `View ${artwork.story_count} Stories`}
              </button>
              
              <button 
                onClick={() => {
                  const { eventsPath } = getNavigationContext();
                  navigate(`${eventsPath}/${event?.id}/stories/create?artworkId=${artwork.vc_id}`);
                }}
                className="btn-secondary"
                title="Add Another Story"
              >
                <Plus className="w-4 h-4" />
                Add Story
              </button>
            </div>
          ) : (
            <button 
              onClick={() => {
                const { eventsPath } = getNavigationContext();
                navigate(`${eventsPath}/${event?.id}/stories/create?artworkId=${artwork.vc_id}`);
              }}
              className="btn-secondary"
              title="Add Story"
            >
              <FileText className="w-4 h-4" />
              Add Story
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="event-detail-admin">
        <div className="loading-state">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <h3>Loading Event Details...</h3>
            
            {/* Progress indicator */}
            <div className="loading-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${loadingProgress}%` }}
                ></div>
              </div>
              <p className="progress-text">{loadingProgress}% Complete</p>
            </div>
            
            {/* Stage indicators */}
            <div className="loading-stages">
              <div className={`stage-item ${!loadingStages.event ? 'completed' : 'active'}`}>
                {!loadingStages.event ? <CheckCircle /> : <Clock />}
                <span>Event Info</span>
              </div>
              <div className={`stage-item ${!loadingStages.artworks ? 'completed' : loadingStages.event ? 'pending' : 'active'}`}>
                {!loadingStages.artworks ? <CheckCircle /> : loadingStages.event ? <Clock /> : <div className="mini-spinner"></div>}
                <span>Artworks</span>
              </div>
              <div className={`stage-item ${!loadingStages.stories ? 'completed' : (loadingStages.event || loadingStages.artworks) ? 'pending' : 'active'}`}>
                {!loadingStages.stories ? <CheckCircle /> : (loadingStages.event || loadingStages.artworks) ? <Clock /> : <div className="mini-spinner"></div>}
                <span>Stories</span>
              </div>
              <div className={`stage-item ${!loadingStages.attendees ? 'completed' : (loadingStages.event || loadingStages.artworks || loadingStages.stories) ? 'pending' : 'active'}`}>
                {!loadingStages.attendees ? <CheckCircle /> : (loadingStages.event || loadingStages.artworks || loadingStages.stories) ? <Clock /> : <div className="mini-spinner"></div>}
                <span>Attendees</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-detail-admin">
        <div className="error-state">
          <AlertCircle className="error-icon" />
          <h3>Error Loading Event</h3>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            <ArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-detail-admin">
        <div className="error-state">
          <AlertCircle className="error-icon" />
          <h3>Event Not Found</h3>
          <p>The requested event could not be found.</p>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            <ArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  const availableTabs = getAvailableTabs();

  return (
    <div className="event-detail-admin">
      {/* Breadcrumb Navigation */}
      <div className="detail-breadcrumb">
        {event.collection_id && (
          <button 
            onClick={() => {
              const { collectionsPath } = getNavigationContext();
              navigate(`${collectionsPath}/${event.collection_id}`);
            }}
            className="breadcrumb-link"
          >
            <ArrowLeft size={14} />
            Back to Collection
          </button>
        )}
        {!event.collection_id && (
          <button onClick={() => navigate(-1)} className="breadcrumb-link">
            <ArrowLeft size={14} />
            Back
          </button>
        )}
      </div>

      {/* Header */}
      <div className="detail-header">
        <div className="detail-header-content">
          <div className="detail-header-left">
            <div className="detail-title-section">
              <div className="detail-meta">
                <span className={`event-type status-${event.status}`}>
                  {event.status === 'draft' && <Edit size={14} />}
                  {event.status || 'Event'}
                </span>
                {event.status === 'draft' && (
                  <span className="draft-notice">
                    <AlertCircle size={14} />
                    This event is not yet visible to users
                  </span>
                )}
              </div>
              <h1 className="detail-title">{event.name}</h1>
              <div 
                className="detail-description"
                dangerouslySetInnerHTML={{ __html: event.description || '' }}
              />
            </div>
          </div>
          
          <div className="detail-header-actions">
            {/* Edit Button */}
            <button 
              onClick={() => navigate(`/curator/events/${eventId}/edit`)}
              className="btn-secondary"
            >
              <Edit size={14} />
              Edit Event
            </button>
            
            {/* Status Management */}
            {event.status === 'draft' ? (
              <button 
                onClick={() => handleStatusChange('active')}
                className="btn-primary publish-btn"
                disabled={updatingStatus}
              >
                {updatingStatus ? <RefreshCw size={14} className="spinning" /> : <CheckCircle size={14} />}
                Publish Event
              </button>
            ) : (
              <div className="status-dropdown">
                <select
                  value={event.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updatingStatus}
                  className="status-select"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            )}
            
            <button 
              onClick={() => {
                const { collectionsPath } = getNavigationContext();
                navigate(`${collectionsPath}/${event.collection_id}`);
              }}
              className="btn-secondary"
              disabled={!event.collection_id}
              title={event.collection_id ? "View Collection" : "No collection linked"}
            >
              <BookOpen size={14} />
              View Collection
            </button>
          </div>
        </div>
      </div>

      {/* Stats Row - Simple Inline */}
      <div className="detail-stats-inline">
        <span className="stat-inline"><Ticket size={14} /> <strong>{ticketSales.filter(s => s.payment_status === 'completed').reduce((sum, s) => sum + (s.quantity || 1), 0)}</strong> Tickets Sold</span>
        <span className="stat-inline"><DollarSign size={14} /> <strong>${ticketSales.filter(s => s.payment_status === 'completed').reduce((sum, s) => sum + parseFloat(s.total_amount || 0), 0).toFixed(2)}</strong> Revenue</span>
        <span className="stat-inline"><Printer size={14} /> <strong>{printSignupStats.printLabSupport}</strong> Any PrintLab Support</span>
        <span className="stat-inline"><ShoppingBag size={14} /> <strong>{printSignupStats.printsOnHand}</strong> Has Prints On Hand</span>
        <span className="stat-inline"><MapPin size={14} /> {event.gallery_name}</span>
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        {availableTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
          >
            <tab.icon className="tab-icon" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="detail-content">
        {/* Overview Tab */}
        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <div className="tickets-tab">
            {/* Ticket Stats */}
            <div className="ticket-stats-grid">
              <div className="ticket-stat-card revenue">
                <div className="ticket-stat-icon">
                  <DollarSign size={14} />
                </div>
                <div className="ticket-stat-content">
                  <div className="ticket-stat-value">{formatCurrency(ticketAnalytics?.totalRevenue)}</div>
                  <div className="ticket-stat-label">Total Revenue</div>
                </div>
              </div>
              
              <div className="ticket-stat-card sold">
                <div className="ticket-stat-icon">
                  <Ticket size={14} />
                </div>
                <div className="ticket-stat-content">
                  <div className="ticket-stat-value">
                    {ticketAnalytics?.totalTicketsSold || 0}
                    <span className="ticket-stat-total">/ {ticketAnalytics?.totalTicketsAvailable || 0}</span>
                  </div>
                  <div className="ticket-stat-label">Tickets Sold</div>
                </div>
              </div>
              
              <div className="ticket-stat-card checkins">
                <div className="ticket-stat-icon">
                  <UserCheck size={14} />
                </div>
                <div className="ticket-stat-content">
                  <div className="ticket-stat-value">{ticketAnalytics?.checkedInCount || 0}</div>
                  <div className="ticket-stat-label">Checked In</div>
                </div>
              </div>
              
              <div className="ticket-stat-card types">
                <div className="ticket-stat-icon">
                  <Tag size={14} />
                </div>
                <div className="ticket-stat-content">
                  <div className="ticket-stat-value">{ticketTypes.length}</div>
                  <div className="ticket-stat-label">Ticket Types</div>
                </div>
              </div>
            </div>

            {/* Eventbrite Sync Banner */}
            {event?.eventbrite_id && (
              <div className="eventbrite-banner">
                <div className="eventbrite-info">
                  <ExternalLink className="eventbrite-icon" />
                  <div>
                    <strong>Eventbrite Connected</strong>
                    <span>ID: {event.eventbrite_id}</span>
                  </div>
                </div>
                <button
                  onClick={handleEventbriteSync}
                  className="btn-secondary"
                  disabled={syncingEventbrite}
                >
                  <RefreshCw className={syncingEventbrite ? 'spinning' : ''} />
                  {syncingEventbrite ? 'Syncing...' : 'Sync Attendees'}
                </button>
              </div>
            )}

            {/* Eventbrite Status Toast */}
            {eventbriteStatus && (
              <div className={`status-toast ${eventbriteStatus.success ? 'success' : 'error'}`}>
                {eventbriteStatus.success ? <CheckCircle /> : <AlertCircle />}
                {eventbriteStatus.message}
              </div>
            )}

            {/* Entertainment Tax Setting */}
            <div className="tax-setting-bar">
              <div className="tax-setting-content">
                <div className="tax-setting-info">
                  <span className="tax-label">Chicago Entertainment Tax (10.25%)</span>
                  <span className="tax-description">Added as a separate line item at checkout for physical events</span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={entertainmentTaxEnabled}
                    onChange={e => handleToggleEntertainmentTax(e.target.checked)}
                    disabled={savingTax}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>

            {/* Ticket Types Section */}
            <div className="ticket-types-section">
              <div className="section-header">
                <h3>Ticket Types</h3>
                <div className="section-actions">
                  <button
                    onClick={() => copyTicketLink()}
                    className="btn-secondary"
                  >
                    <Link size={14} /> Copy Ticket Link
                  </button>
                  <button
                    onClick={() => {
                      resetTicketTypeForm();
                      setEditingTicketType(null);
                      setShowTicketTypeModal(true);
                    }}
                    className="btn-primary"
                  >
                    <Plus size={14} /> Add Ticket Type
                  </button>
                </div>
              </div>

              {ticketTypes.length === 0 ? (
                <div className="empty-tickets">
                  <Ticket className="empty-icon" />
                  <h4>No Ticket Types Yet</h4>
                  <p>Create ticket types to start selling tickets for this event.</p>
                  <button
                    onClick={() => {
                      resetTicketTypeForm();
                      setShowTicketTypeModal(true);
                    }}
                    className="btn-primary"
                  >
                    <Plus size={14} /> Create First Ticket Type
                  </button>
                </div>
              ) : (
                <div className="ticket-types-grid">
                  {ticketTypes.map(type => {
                    const soldPercentage = (type.tickets_sold / type.tickets_available) * 100;
                    const isEarlyBird = type.early_bird_deadline && new Date(type.early_bird_deadline) > new Date();
                    const currentPrice = isEarlyBird && type.early_bird_price
                      ? type.early_bird_price
                      : type.regular_price;

                    return (
                      <div key={type.id} className={`ticket-type-card ${type.is_active === false ? 'inactive' : ''}`}>
                        <div className="ticket-type-header">
                          <h4>{type.ticket_type_name}</h4>
                          <div className="ticket-type-actions">
                            <button onClick={() => copyTicketLink(type.id)} title="Copy link">
                              <Copy size={14} />
                            </button>
                            <button onClick={() => openEditTicketType(type)} title="Edit">
                              <Edit size={14} />
                            </button>
                            <button onClick={() => handleDeleteTicketType(type.id)} title="Delete" className="delete-btn">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        <div className="ticket-price-section">
                          <span className="current-price">{formatCurrency(currentPrice)}</span>
                          {isEarlyBird && type.early_bird_price && (
                            <span className="early-bird-info">
                              <Percent size={10} />
                              Early Bird
                            </span>
                          )}
                        </div>

                        {type.description && (
                          <p className="ticket-type-description">{type.description}</p>
                        )}

                        <div className="ticket-availability">
                          <div className="progress-bar">
                            <div
                              className="progress-fill"
                              style={{ width: `${Math.min(soldPercentage, 100)}%` }}
                            />
                          </div>
                          <div className="availability-text">
                            <span><strong>{type.tickets_sold}</strong> sold</span>
                            <span><strong>{type.tickets_available - type.tickets_sold}</strong> remaining</span>
                          </div>
                        </div>

                        {type.early_bird_deadline && (
                          <div className="deadline-info">
                            <Clock size={10} />
                            <span>
                              Ends {new Date(type.early_bird_deadline).toLocaleDateString()}
                            </span>
                          </div>
                        )}

                        {type.is_active === false && (
                          <div className="inactive-badge">
                            <EyeOff size={10} /> Inactive
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Promo Codes Section */}
            <div className="promo-codes-section">
              <div className="section-header">
                <h3>Promo Codes</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {event?.collection_id && (
                    <button
                      onClick={() => setShowArtistPromoModal(true)}
                      className="btn-secondary"
                    >
                      <Users size={14} /> Artist Promos
                    </button>
                  )}
                  <button
                    onClick={() => setShowPromoCodeModal(true)}
                    className="btn-primary"
                  >
                    <Plus size={14} /> Add Promo Code
                  </button>
                </div>
              </div>
              
              {promoCodes.length === 0 ? (
                <div className="empty-promo-codes">
                  <Percent className="empty-icon" />
                  <h4>No Promo Codes</h4>
                  <p>Create discount codes for this event.</p>
                </div>
              ) : (
                <>
                  {/* General Promo Codes */}
                  {generalPromoCodes.length > 0 && (
                    <div className="promo-codes-grid">
                      {generalPromoCodes.map(promo => (
                        <div key={promo.id} className="promo-code-card">
                          <div className="promo-code-header">
                            <div className="promo-code-badge">
                              <Tag className="w-4 h-4" />
                              <span className="promo-code-text">{promo.promo_code}</span>
                            </div>
                            <button
                              onClick={() => handleDeactivatePromoCode(promo.id, promo.promo_code)}
                              className="deactivate-btn"
                              title="Deactivate"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <div className="promo-discount">
                            {promo.discount_type === 'percentage' 
                              ? `${promo.discount_value}% off`
                              : formatCurrency(promo.discount_value) + ' off'
                            }
                          </div>
                          
                          <div className="promo-details">
                            <div className="promo-usage">
                              <span>Used: {promo.current_uses || 0}</span>
                              {promo.max_uses && (
                                <span> / {promo.max_uses} max</span>
                              )}
                            </div>
                            {promo.valid_until && (
                              <div className={`promo-expiry ${new Date(promo.valid_until) < new Date() ? 'expired' : ''}`}>
                                <Clock size={10} />
                                {new Date(promo.valid_until) < new Date() 
                                  ? 'Expired' 
                                  : `Expires ${new Date(promo.valid_until).toLocaleDateString()}`
                                }
                              </div>
                            )}
                          </div>
                          
                          <div className="promo-actions">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(promo.promo_code);
                                setEventbriteStatus({ success: true, message: 'Promo code copied!' });
                                setTimeout(() => setEventbriteStatus(null), 2000);
                              }}
                              className="copy-promo-btn"
                              title="Copy code"
                            >
                              <Copy className="w-3 h-3" /> Copy Code
                            </button>
                            <button
                              onClick={() => {
                                const ticketUrl = `https://share.vibrantcast.com/event?id=${eventId}&promo=${promo.promo_code}`;
                                navigator.clipboard.writeText(ticketUrl);
                                setEventbriteStatus({ success: true, message: 'Ticket link with promo copied!' });
                                setTimeout(() => setEventbriteStatus(null), 2000);
                              }}
                              className="copy-promo-btn"
                              title="Copy ticket link with promo pre-applied"
                            >
                              <Link className="w-3 h-3" /> Copy Link
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Artist Promo Codes - Grouped */}
                  {Object.keys(artistPromosByArtist).length > 0 && (
                    <div className="artist-promos-section">
                      <h4 style={{ margin: '24px 0 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Users size={16} /> Artist Promo Codes ({artistPromoCodes.length} codes for {Object.keys(artistPromosByArtist).length} artists)
                      </h4>
                      <div className="artist-promos-list">
                        {Object.entries(artistPromosByArtist).map(([artistUuid, codes]) => (
                          <details key={artistUuid} className="artist-promo-group">
                            <summary className="artist-promo-summary">
                              <span className="artist-promo-name">
                                {codes[0]?.promo_code?.split('-')[0] || 'Artist'}
                              </span>
                              <span className="artist-promo-count">
                                {codes.length} codes · {codes.reduce((sum, c) => sum + (c.current_uses || 0), 0)} used
                              </span>
                            </summary>
                            <div className="artist-promo-codes">
                              {codes.map(promo => (
                                <div key={promo.id} className="artist-promo-code-row">
                                  <span className="promo-code-mono">{promo.promo_code}</span>
                                  <span className="promo-discount-inline">
                                    {promo.discount_type === 'percentage' 
                                      ? `${promo.discount_value}%` 
                                      : `$${promo.discount_value}`}
                                  </span>
                                  <span className="promo-usage-inline">
                                    {promo.current_uses || 0}{promo.max_uses ? `/${promo.max_uses}` : ''} used
                                  </span>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(promo.promo_code);
                                      setEventbriteStatus({ success: true, message: `Code "${promo.promo_code}" copied!` });
                                      setTimeout(() => setEventbriteStatus(null), 2000);
                                    }}
                                    className="copy-promo-btn-sm"
                                    title="Copy promo code"
                                  >
                                    <Copy size={12} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      const ticketUrl = `https://share.vibrantcast.com/event?id=${eventId}&promo=${promo.promo_code}`;
                                      navigator.clipboard.writeText(ticketUrl);
                                      setEventbriteStatus({ success: true, message: `Link copied for ${promo.promo_code}!` });
                                      setTimeout(() => setEventbriteStatus(null), 2000);
                                    }}
                                    className="copy-promo-btn-sm"
                                    title="Copy shareable link"
                                  >
                                    <Link size={12} />
                                  </button>
                                  <button
                                    onClick={() => handleDeactivatePromoCode(promo.id, promo.promo_code)}
                                    className="deactivate-btn-sm"
                                    title="Deactivate"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </details>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Recent Sales Section */}
            {ticketSales.length > 0 && (
              <div className="recent-sales-section">
                <div className="section-header">
                  <h3>Recent Sales</h3>
                  <span className="sales-count">
                    {ticketSales.filter(s => s.payment_status === 'completed').length} completed orders
                  </span>
                </div>

                <div className="sales-table">
                  <div className="sales-table-header">
                    <div className="col-customer">Customer</div>
                    <div className="col-ticket">Ticket Type</div>
                    <div className="col-qty">Qty</div>
                    <div className="col-amount">Amount</div>
                    <div className="col-status">Status</div>
                    <div className="col-date">Date</div>
                  </div>
                  {ticketSales.slice((salesPage - 1) * SALES_PER_PAGE, salesPage * SALES_PER_PAGE).map(sale => (
                    <div key={sale.id} className="sales-table-row">
                      <div className="col-customer">
                        <div className="customer-info">
                          {sale.user?.profile_img ? (
                            <img src={sale.user.profile_img} alt="" className="customer-avatar" />
                          ) : (
                            <div className="customer-avatar placeholder">
                              {sale.user?.first_name?.[0] || '?'}
                            </div>
                          )}
                          <div>
                            <div className="customer-name">
                              {sale.user?.first_name} {sale.user?.last_name}
                            </div>
                            <div className="customer-email">{sale.user?.email}</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-ticket">
                        {sale.ticket_type?.ticket_type_name || sale.ticket_type || 'General'}
                      </div>
                      <div className="col-qty">{sale.quantity || 1}</div>
                      <div className="col-amount">{formatCurrency(sale.amount_paid)}</div>
                      <div className="col-status">
                        <span className={`status-pill ${sale.payment_status}`}>
                          {sale.payment_status}
                        </span>
                      </div>
                      <div className="col-date">
                        {new Date(sale.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pagination Controls */}
                {ticketSales.length > SALES_PER_PAGE && (
                  <div className="pagination-controls">
                    <button
                      onClick={() => setSalesPage(p => Math.max(1, p - 1))}
                      disabled={salesPage === 1}
                      className="pagination-btn"
                    >
                      Previous
                    </button>
                    <span className="pagination-info">
                      Page {salesPage} of {Math.ceil(ticketSales.length / SALES_PER_PAGE)} ({ticketSales.length} total)
                    </span>
                    <button
                      onClick={() => setSalesPage(p => Math.min(Math.ceil(ticketSales.length / SALES_PER_PAGE), p + 1))}
                      disabled={salesPage >= Math.ceil(ticketSales.length / SALES_PER_PAGE)}
                      className="pagination-btn"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Attendees Tab */}
        {activeTab === 'attendees' && (
          <div className="attendees-tab">
            <div className="attendees-controls">
              <h3>Ticket Sales ({ticketSales.filter(s => s.payment_status === 'completed').reduce((sum, s) => sum + (s.quantity || 1), 0)})</h3>
              <button
                onClick={() => handlePrintChecklist()}
                className="btn-secondary print-btn"
              >
                <Printer size={16} />
                Print Check-in List
              </button>
            </div>
            <div className="attendees-list">
              {ticketSales.filter(s => s.payment_status === 'completed').map((sale, index) => (
                <div key={`${sale.id}-${index}`} className="attendee-item">
                  <div className="attendee-info">
                    <span className="attendee-name">
                      {sale.customer_name || sale.user?.first_name + ' ' + sale.user?.last_name || 'Guest'}
                    </span>
                    <span className="attendee-email">{sale.customer_email || sale.user?.email || '—'}</span>
                    <span className="attendee-tickets">
                      {sale.quantity || 1} × {sale.ticket_type_name || sale.ticket_type?.ticket_type_name || 'General'}
                      {sale.source === 'eventbrite' && <span className="source-badge eb">EB</span>}
                      {sale.source === 'stripe' && <span className="source-badge vc">VC</span>}
                    </span>
                  </div>
                  <div className="attendee-status">
                    <span className={`status-badge ${sale.checked_in ? 'checked-in' : 'pending'}`}>
                      {sale.checked_in ? 'Checked In' : 'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {ticketSales.filter(s => s.payment_status === 'completed').length === 0 && (
              <div className="empty-attendees">
                <Users className="empty-icon" />
                <p>No ticket sales found for this event.</p>
              </div>
            )}
          </div>
        )}

        {/* Merchandise Tab */}
        {activeTab === 'merchandise' && (
          <EventMerchTab eventId={event?.id} />
        )}

        {/* Print Signups Tab */}
        {activeTab === 'print_signups' && (
          <EventPrintSignupsTab eventId={event?.id} collectionId={event?.collection_id} />
        )}
      </div>

      {/* Artwork Detail Modal */}
      {selectedArtwork && (
        <div className="artwork-modal" onClick={() => setSelectedArtwork(null)}>
          <div className="artwork-modal-content" onClick={e => e.stopPropagation()}>
            <div className="artwork-modal-header">
              <h3>{selectedArtwork.title}</h3>
              <button onClick={() => setSelectedArtwork(null)}>×</button>
            </div>
            <div className="artwork-modal-body">
              <div className="artwork-modal-image">
                <img src={selectedArtwork.cover_image} alt={selectedArtwork.title} />
              </div>
              <div className="artwork-modal-details">
                <p><strong>Artist:</strong> {
                  selectedArtwork.artist ? 
                    (selectedArtwork.artist.nickname || `${selectedArtwork.artist.first_name} ${selectedArtwork.artist.last_name}`) :
                    selectedArtwork.artist_name || 'Unknown Artist'
                }</p>
                {selectedArtwork.dimensions && (
                  <p><strong>Dimensions:</strong> {selectedArtwork.dimensions}</p>
                )}
                {selectedArtwork.price && <p><strong>Price:</strong> ${selectedArtwork.price}</p>}
                {selectedArtwork.description && <p><strong>Description:</strong> {selectedArtwork.description}</p>}
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Stories Selection Modal */}
      {selectedArtworkStories && (
        <div className="modal-overlay" onClick={() => setSelectedArtworkStories(null)}>
          <div className="modal-content stories-selection-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Stories for "{selectedArtworkStories.title}"</h3>
              <button 
                onClick={() => setSelectedArtworkStories(null)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="stories-list">
                {selectedArtworkStories.stories.map((story) => (
                  <div key={story.story_id} className="story-item">
                    <div className="story-item-info">
                      <h4 className="story-item-title">{story.title}</h4>
                      <p className="story-item-writer">
                        by {story.story_writer ? 
                          (story.story_writer.nickname || `${story.story_writer.first_name} ${story.story_writer.last_name}`) :
                          'Unknown Writer'
                        }
                      </p>
                      <p className="story-item-date">
                        Created {new Date(story.created_at).toLocaleDateString()}
                      </p>
                      {story.featured && (
                        <span className="featured-badge">
                          <Star className="w-3 h-3" />
                          Featured
                        </span>
                      )}
                    </div>
                    <div className="story-item-actions">
                      <button 
                        onClick={() => {
                          const { storiesPath } = getNavigationContext();
                          navigate(`${storiesPath}/${story.story_id}`);
                        }}
                        className="btn-primary"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                      <button 
                        onClick={() => {
                          const { storiesPath } = getNavigationContext();
                          navigate(`${storiesPath}/${story.story_id}/edit`);
                        }}
                        className="btn-secondary"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="modal-actions">
                <button 
                  onClick={() => {
                    const { eventsPath } = getNavigationContext();
                    navigate(`${eventsPath}/${event?.id}/stories/create?artworkId=${selectedArtworkStories.vc_id}`);
                  }}
                  className="btn-secondary"
                >
                  <Plus className="w-4 h-4" />
                  Add Another Story
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promo Code Modal */}
      {showPromoCodeModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowPromoCodeModal(false)}>
          <div className="modal-content promo-code-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Promo Code</h2>
              <button onClick={() => setShowPromoCodeModal(false)} className="close-btn">
                <X />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Promo Code *</label>
                <input
                  type="text"
                  value={promoCodeForm.promoCode}
                  onChange={e => setPromoCodeForm({ ...promoCodeForm, promoCode: e.target.value.toUpperCase() })}
                  placeholder="e.g., ARTSPEAKS50"
                  style={{ textTransform: 'uppercase' }}
                />
                <small>Customers will enter this code at checkout</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Discount Type *</label>
                  <select
                    value={promoCodeForm.discountType}
                    onChange={e => setPromoCodeForm({ ...promoCodeForm, discountType: e.target.value })}
                  >
                    <option value="percentage">Percentage Off</option>
                    <option value="fixed">Fixed Amount Off</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Discount Value *</label>
                  <div className="input-with-icon">
                    {promoCodeForm.discountType === 'percentage' ? <Percent /> : <DollarSign />}
                    <input
                      type="number"
                      min="0"
                      max={promoCodeForm.discountType === 'percentage' ? 100 : undefined}
                      step={promoCodeForm.discountType === 'percentage' ? 1 : 0.01}
                      value={promoCodeForm.discountValue}
                      onChange={e => setPromoCodeForm({ ...promoCodeForm, discountValue: e.target.value })}
                      placeholder={promoCodeForm.discountType === 'percentage' ? '50' : '10.00'}
                    />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Max Redemptions (Optional)</label>
                  <input
                    type="number"
                    min="1"
                    value={promoCodeForm.maxRedemptions}
                    onChange={e => setPromoCodeForm({ ...promoCodeForm, maxRedemptions: e.target.value })}
                    placeholder="Unlimited"
                  />
                  <small>Total times this code can be used</small>
                </div>

                <div className="form-group">
                  <label>Expires On (Optional)</label>
                  <input
                    type="datetime-local"
                    value={promoCodeForm.validUntil}
                    onChange={e => setPromoCodeForm({ ...promoCodeForm, validUntil: e.target.value })}
                  />
                  <small>Code becomes invalid after this date</small>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowPromoCodeModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleCreatePromoCode}
                className="btn-primary"
                disabled={savingPromoCode || !promoCodeForm.promoCode || !promoCodeForm.discountValue}
              >
                {savingPromoCode ? 'Creating...' : 'Create Promo Code'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Artist Promo Modal */}
      {showArtistPromoModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowArtistPromoModal(false)}>
          <div className="modal-content promo-code-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Generate Artist Promo Codes</h2>
              <button onClick={() => setShowArtistPromoModal(false)} className="close-btn">
                <X />
              </button>
            </div>

            <div className="modal-body">
              <p style={{ color: '#94a3b8', marginBottom: '16px' }}>
                Generate a personalized promo code for each artist in this event.
                Code pattern: LASTNAME-DISCOUNT (e.g., SMITH-20OFF)
              </p>

              {artistPromoTiers.map((tier, idx) => (
                <div key={idx} className="artist-tier-row" style={{ display: 'flex', gap: '12px', alignItems: 'end', marginBottom: '12px', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Type</label>
                    <select
                      value={tier.discountType}
                      onChange={e => {
                        const updated = [...artistPromoTiers];
                        updated[idx].discountType = e.target.value;
                        setArtistPromoTiers(updated);
                      }}
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed $</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Value</label>
                    <input
                      type="number"
                      min="0"
                      value={tier.discountValue}
                      onChange={e => {
                        const updated = [...artistPromoTiers];
                        updated[idx].discountValue = e.target.value;
                        setArtistPromoTiers(updated);
                      }}
                      placeholder={tier.discountType === 'percentage' ? '20' : '10'}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Max Uses</label>
                    <input
                      type="number" min="1"
                      value={tier.maxRedemptionsPerCode}
                      onChange={e => {
                        const updated = [...artistPromoTiers];
                        updated[idx].maxRedemptionsPerCode = e.target.value;
                        setArtistPromoTiers(updated);
                      }}
                      placeholder="10"
                    />
                  </div>
                  {artistPromoTiers.length > 1 && (
                    <button
                      onClick={() => setArtistPromoTiers(artistPromoTiers.filter((_, i) => i !== idx))}
                      className="deactivate-btn"
                      style={{ marginBottom: '2px' }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}

              <button
                onClick={() => setArtistPromoTiers([...artistPromoTiers, { discountType: 'percentage', discountValue: 10, codesPerArtist: 1, maxRedemptionsPerCode: 10 }])}
                className="btn-secondary"
                style={{ marginBottom: '16px' }}
              >
                <Plus size={14} /> Add Tier
              </button>

              <div className="form-group">
                <label>Expires On (Optional)</label>
                <input
                  type="datetime-local"
                  value={artistPromoExpiry}
                  onChange={e => setArtistPromoExpiry(e.target.value)}
                />
              </div>

              {artistPromoResults && (
                <div style={{ padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', marginTop: '12px' }}>
                  <strong style={{ color: '#10b981' }}>Generated {artistPromoResults.totalCodesCreated} codes</strong>
                  <span style={{ color: '#94a3b8' }}> for {artistPromoResults.totalArtists} artists</span>
                  {artistPromoResults.totalErrors > 0 && (
                    <span style={{ color: '#ef4444', marginLeft: '8px' }}>({artistPromoResults.totalErrors} errors)</span>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowArtistPromoModal(false)} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleGenerateArtistPromos}
                className="btn-primary"
                disabled={generatingArtistPromos}
              >
                {generatingArtistPromos ? 'Generating...' : 'Generate Artist Promos'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Ticket Type Modal */}
      {showTicketTypeModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowTicketTypeModal(false)}>
          <div className="modal-content ticket-type-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTicketType ? 'Edit Ticket Type' : 'Create Ticket Type'}</h2>
              <button 
                onClick={() => {
                  setShowTicketTypeModal(false);
                  setEditingTicketType(null);
                  resetTicketTypeForm();
                }} 
                className="close-btn"
              >
                <X />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Ticket Name *</label>
                <input
                  type="text"
                  value={ticketTypeForm.ticket_type_name}
                  onChange={e => setTicketTypeForm({ ...ticketTypeForm, ticket_type_name: e.target.value })}
                  placeholder="e.g., General Admission, VIP, Early Bird"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={ticketTypeForm.description}
                  onChange={e => setTicketTypeForm({ ...ticketTypeForm, description: e.target.value })}
                  placeholder="What's included with this ticket?"
                  rows={2}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Regular Price *</label>
                  <div className="input-with-icon">
                    <DollarSign />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={ticketTypeForm.regular_price}
                      onChange={e => setTicketTypeForm({ ...ticketTypeForm, regular_price: e.target.value })}
                      placeholder="25.00"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Tickets Available *</label>
                  <input
                    type="number"
                    min="1"
                    value={ticketTypeForm.tickets_available}
                    onChange={e => setTicketTypeForm({ ...ticketTypeForm, tickets_available: e.target.value })}
                    placeholder="100"
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <h4>Early Bird Pricing (Optional)</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Early Bird Price</label>
                    <div className="input-with-icon">
                      <DollarSign />
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={ticketTypeForm.early_bird_price}
                        onChange={e => setTicketTypeForm({ ...ticketTypeForm, early_bird_price: e.target.value })}
                        placeholder="20.00"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Early Bird Deadline</label>
                    <input
                      type="datetime-local"
                      value={ticketTypeForm.early_bird_deadline}
                      onChange={e => setTicketTypeForm({ ...ticketTypeForm, early_bird_deadline: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Sale Period (Optional)</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Sale Start</label>
                    <input
                      type="datetime-local"
                      value={ticketTypeForm.sale_start_date}
                      onChange={e => setTicketTypeForm({ ...ticketTypeForm, sale_start_date: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Sale End</label>
                    <input
                      type="datetime-local"
                      value={ticketTypeForm.sale_end_date}
                      onChange={e => setTicketTypeForm({ ...ticketTypeForm, sale_end_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={ticketTypeForm.is_active !== false}
                    onChange={e => setTicketTypeForm({ ...ticketTypeForm, is_active: e.target.checked })}
                  />
                  <span>Active (visible and available for purchase)</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                onClick={() => {
                  setShowTicketTypeModal(false);
                  setEditingTicketType(null);
                  resetTicketTypeForm();
                }} 
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTicketType}
                className="btn-primary"
                disabled={savingTicket || !ticketTypeForm.ticket_type_name || !ticketTypeForm.regular_price}
              >
                {savingTicket ? 'Saving...' : editingTicketType ? 'Update Ticket Type' : 'Create Ticket Type'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default EventDetailAdmin;